import { useCookieState } from 'ahooks'
import * as React from 'react'
import useSWR, { KeyedMutator, SWRConfiguration } from 'swr'

import { ErrorHttpRequest } from './errors'
import { HttpMethods } from '@/config/urls'

export const HEADERS = {
  ACCEPT: 'Accept',
  CONTENT_TYPE: 'Content-Type',
  X_XSRF_TOKEN: 'X-XSRF-TOKEN',
}

export const COOKIES = {
  XSRF_TOKEN: 'XSRF-TOKEN',
  LOGIN_EMAIL: 'loginEmail',
}

type useHttpFetcherParams = {
  customSwrConfig?: SWRConfiguration
  headers?: Headers
  init?: RequestInit
  url: string
}

export type FetcherResponse<T> = {
  data?: T
  error: ErrorHttpRequest
  isLoading: boolean
  isValidating: boolean
  mutate: KeyedMutator<T>
}

/*
 * useHttpFetcher is a custom React hook to fecth and cache data using SWR.
 */
export function useHttpFetcher<T>({
  customSwrConfig,
  headers,
  init,
  url,
}: useHttpFetcherParams): FetcherResponse<T> {
  const abortController = React.useRef<AbortController | null>()
  const [xsrfToken] = useCookieState(COOKIES.XSRF_TOKEN)
  const swrCacheId = init?.body ? url + init.body : url

  const { data, error, mutate, isValidating } = useSWR<T>(
    swrCacheId,
    () =>
      httpFetchWithErrorHandling(
        url,
        { headers, init, xsrfToken },
        abortController
      ),
    customSwrConfig
  )

  return {
    data,
    error,
    isLoading: !!swrCacheId && !data && !error,
    isValidating,
    mutate,
  }
}

type httpFetcherParams = {
  init?: RequestInit
  headers?: Headers
  method?: HttpMethods
  xsrfToken?: string
}

export function httpFetchWithErrorHandling<T>(
  url: string,
  params?: httpFetcherParams,
  abortController?: any
): Promise<T> {
  const defaultHeaders = new Headers({
    [HEADERS.ACCEPT]: 'application/json',
    [HEADERS.CONTENT_TYPE]: 'application/json',
    [HEADERS.X_XSRF_TOKEN]: params?.xsrfToken,
  })

  let requestInit = params?.init

  if (abortController) {
    const controller = new AbortController()
    if (abortController.current) {
      abortController.current.abort()
    }
    abortController.current = controller
    requestInit = Object.assign({}, { signal: controller.signal }, params.init)
  }

  const headers = params?.headers || defaultHeaders
  return (
    url &&
    fetch(url, {
      headers,
      method: params?.method || HttpMethods.GET,
      ...requestInit, // the above params are overwritten by the init param
    }).then((response) => {
      if (response.ok) {
        if (headers && headers.get(HEADERS.CONTENT_TYPE).indexOf('json') < 0) {
          return response.text() as unknown as T
        }
        return response.json() as unknown as T
      } else {
        return response.text().then((text) => {
          throw new ErrorHttpRequest(response.statusText, text, response.status)
        })
      }
    })
  )
}

/*
 * httpFetch is a utility function to trigger and forget API calls.
 */
export function httpFetch(
  url: string,
  params?: httpFetcherParams,
  abortController?: any
): Promise<Response> {
  const defaultHeaders = new Headers({
    [HEADERS.ACCEPT]: 'application/json',
    [HEADERS.CONTENT_TYPE]: 'application/json',
    [HEADERS.X_XSRF_TOKEN]: params?.xsrfToken,
  })

  let requestInit = params?.init

  if (abortController) {
    const controller = new AbortController()
    if (abortController.current) {
      abortController.current.abort()
    }
    abortController.current = controller
    requestInit = Object.assign({}, { signal: controller.signal }, params.init)
  }

  return (
    url &&
    fetch(url, {
      headers: params?.headers || defaultHeaders,
      method: params?.method || HttpMethods.GET,
      ...requestInit, // the above params are overwritten by the init param
    })
  )
}
